import React, { useContext } from "react";
import { DataTable, OneLineShowMore, Table } from "@sscs/ui";
import { getClassName } from "@sscs/utils";
import { REPORT_PAGES_ROUTES } from "../../../../constants";
import { expandedRowPaginationUpdated, expandedRowsUpdated, focusedRowUpdated, selectExpandedRow, selectExpandedRowPagination, selectFocusedRow, } from "../../../../store/issuesDiffSlice";
import { filterByLink } from "../../../../store/issuesSlice";
import { ExpandedRowDetails } from "../../../../ui";
import { estimateTableHeight } from "../../../../utils";
import { LinkToReportTab } from "../../../common";
import { ReportContext } from "../../../context";
import { useReportDispatch, useReportSelector } from "../../../hooks";
import { useGetIssuesDiffSourcesQuery } from "../../../services/diff.api";
import { getQueryStatus } from "../../../utils";
import FileName from "../fileName/FileName";
import styles from "./issueChanges.module.scss";
import { CHANGE_LABEL } from "./utils";
const DETAILS_COLUMNS = [
    { id: "fileName", label: "File name", width: "400px" },
    { id: "filePath", label: "File path", width: "100%" },
];
const EMPHASIZED_CHANGES = ["new", "worsened"];
const PILL_PREFIX = {
    existed: "",
    fixed: "−",
    new: "+",
    now: "",
};
const CountPill = ({ variant, value, dimmed = false, }) => {
    if (value === null)
        return null;
    return (React.createElement("span", { className: getClassName(styles.pill, styles[`pill-${variant}`], {
            [styles["pill-dimmed"]]: dimmed,
        }) },
        PILL_PREFIX[variant],
        value.toLocaleString()));
};
const DetailsRow = ({ policyId, id, resolved, totalCount, }) => {
    const dispatch = useReportDispatch();
    const { reportId, diffId } = useContext(ReportContext);
    const pagination = useReportSelector((state) => selectExpandedRowPagination(state, policyId, resolved ? "resolved" : "introduced"));
    const { data, isFetching, isLoading, isError, error } = useGetIssuesDiffSourcesQuery({
        pagination,
        ui_item_id: policyId,
        diff_type: resolved ? "resolved" : "introduced",
        sdlc_report_id: reportId,
        ui_id: diffId,
    });
    if (!data)
        return (React.createElement("div", { style: { minHeight: `${estimateTableHeight(totalCount)}px` } }));
    return (React.createElement(DataTable, { title: React.createElement("span", null,
            "Files ",
            React.createElement("b", null,
                resolved ? "no longer" : "newly",
                " impacted "),
            "by Issue",
            " ",
            React.createElement("b", null, policyId)), columns: DETAILS_COLUMNS, pagination: {
            id: `diff-${id}-pagination`,
            itemCount: data.match_count,
            pagination,
            onChange: (newPagination) => dispatch(expandedRowPaginationUpdated({
                id: policyId,
                dataType: resolved ? "resolved" : "introduced",
                pagination: newPagination,
            })),
        }, status: getQueryStatus({
            isLoading,
            isFetching,
            isError,
            error,
            data,
        }) }, data.files.map((file, index) => (React.createElement(Table.Row, { hover: true, key: file.id },
        React.createElement(Table.Cell, null,
            React.createElement(FileName, { index: index, file: file })),
        React.createElement(Table.Cell, null,
            React.createElement(OneLineShowMore, { id: `dif-${id}-show-more-less-${index}`, value: file.path })))))));
};
const IssueDetails = ({ rowIndex, issue, }) => {
    var _a, _b;
    const { changeType, id, hasIntroduced, hasResolved } = issue;
    const sliceType = hasIntroduced && !hasResolved ? "introduced" : "resolved";
    const dimmed = changeType === "resolved";
    const dispatch = useReportDispatch();
    const focusedRow = useReportSelector((state) => selectFocusedRow(state, sliceType));
    const isExpanded = useReportSelector((state) => selectExpandedRow(state, id, sliceType));
    return (React.createElement(DataTable.ExpandedRow, { id: `diff-${changeType}-expand-row-${rowIndex}`, expanded: isExpanded, onChange: (newValue) => dispatch(expandedRowsUpdated({
            id,
            expand: newValue,
            dataType: sliceType,
        })), focused: focusedRow === id, onClick: () => dispatch(focusedRowUpdated({
            id,
            dataType: sliceType,
        })) },
        React.createElement(DataTable.ExpandedRow.Summary, null,
            React.createElement(Table.Cell, null,
                React.createElement("span", { className: getClassName(styles["change-label"], {
                        [styles["change-label-emphasized"]]: EMPHASIZED_CHANGES.includes(changeType),
                    }) }, CHANGE_LABEL[changeType])),
            React.createElement(Table.Cell, null, issue.now !== 0 ? (React.createElement(LinkToReportTab, { id: `diff-${changeType}-id-${rowIndex}`, to: REPORT_PAGES_ROUTES.ISSUES_ROUTE, color: "link", onClick: () => dispatch(filterByLink({
                    issue: { operation: "is", values: [id] },
                })), className: styles.link }, id)) : (React.createElement("span", { className: getClassName({ [styles.dimmed]: dimmed }) }, id))),
            React.createElement(Table.Cell, { className: getClassName({ [styles.dimmed]: dimmed }) }, issue.description),
            React.createElement(Table.Cell, { align: "right" },
                React.createElement(CountPill, { variant: "existed", value: issue.existed })),
            React.createElement(Table.Cell, { align: "right" },
                React.createElement(CountPill, { variant: "fixed", value: issue.fixed })),
            React.createElement(Table.Cell, { align: "right" },
                React.createElement(CountPill, { variant: "new", value: issue.added })),
            React.createElement(Table.Cell, { align: "right" },
                React.createElement(CountPill, { variant: "now", value: issue.now, dimmed: dimmed }))),
        React.createElement(DataTable.ExpandedRow.Details, null,
            React.createElement(ExpandedRowDetails, null,
                issue.hasIntroduced && (React.createElement(DetailsRow, { id: `${changeType}-introduced-${rowIndex}`, resolved: false, policyId: id, totalCount: (_a = issue.added) !== null && _a !== void 0 ? _a : 0 })),
                issue.hasResolved && (React.createElement(DetailsRow, { id: `${changeType}-resolved-${rowIndex}`, resolved: true, policyId: id, totalCount: (_b = issue.fixed) !== null && _b !== void 0 ? _b : 0 }))))));
};
export default IssueDetails;
//# sourceMappingURL=IssueDetails.js.map